import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera, Image as ImageIcon, MapPin, Loader2, MessageCircle, Mail, Sparkles,
  ArrowLeft, Check, Trash2, RefreshCw, Send, Phone, Building2, ChevronRight, Clock, Bell,
} from "lucide-react";
import { API } from "./api";
import { uploadMemberFile } from "./authApi";
import { compressImage } from "./imageCompress";
import { VoiceTextBox } from "./AIAssist";

const post = (path, body) =>
  fetch(`${API}${path}`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Erreur"); return r.json(); });

const TONES = [
  { key: "chaleureux", label: "Chaleureux" },
  { key: "professionnel", label: "Professionnel" },
  { key: "premium", label: "Premium" },
  { key: "direct", label: "Direct" },
];

const cleanPhoneIntl = (phone) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = "33" + d.slice(1);
  return d;
};

const STATUSES = [
  { key: "a_contacter", label: "À contacter", cls: "bg-[#C19A6B]/10 text-[#C19A6B]" },
  { key: "contacte", label: "Contacté", cls: "bg-blue-100 text-blue-700" },
  { key: "interesse", label: "Intéressé", cls: "bg-green-100 text-green-700" },
  { key: "a_rappeler", label: "À rappeler", cls: "bg-amber-100 text-amber-700" },
  { key: "refuse", label: "Refusé", cls: "bg-zinc-200 text-zinc-500" },
];
const statusMeta = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];
const todayISO = () => new Date().toISOString().slice(0, 10);
const relanceDue = (p) => !!(p && p.relance_date && p.status !== "refuse" && p.relance_date <= todayISO());

const Field = ({ label, value, onChange, placeholder, testid, type = "text" }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      data-testid={testid}
      className="mt-1 w-full bg-white border border-[#E5E7EB] px-3 py-2.5 text-sm outline-none focus:border-[#C19A6B] rounded-[4px]" />
  </label>
);

export default function ProspectionTerrain() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | wizard
  const [step, setStep] = useState(0); // 0 scan · 1 review · 2 channel · 3 intent · 4 message
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [scanStatus, setScanStatus] = useState("");
  const [retryUrl, setRetryUrl] = useState("");
  const [cur, setCur] = useState(null); // prospect en cours
  const [channel, setChannel] = useState("");
  const [intent, setIntent] = useState("");
  const [tone, setTone] = useState("chaleureux");
  const [gen, setGen] = useState(null); // {subject, message, referral_link, channel}
  const [detail, setDetail] = useState(null); // fiche ouverte (historique)
  const [batch, setBatch] = useState(null); // {done,total,ok} tournée terrain

  const patchProspect = async (id, patch) => {
    const fresh = await fetch(`${API}/prospection/prospects/${id}`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch),
    }).then((r) => r.json());
    setProspects((arr) => arr.map((x) => (x.id === id ? fresh : x)));
    setDetail((d) => (d && d.id === id ? fresh : d));
    return fresh;
  };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/prospection/prospects`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setProspects(Array.isArray(d) ? d : []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetWizard = () => { setStep(0); setCur(null); setChannel(""); setIntent(""); setGen(null); setTone("chaleureux"); setErr(""); };

  const scanOne = async (file) => {
    const compressed = await compressImage(file);
    const up = await uploadMemberFile(compressed);
    return { url: up.url, data: await post("/prospection/scan", { image_url: up.url }) };
  };

  // Analyse une image déjà uploadée (pour la reprise « Réessayer » sans ré-upload).
  const analyzeUrl = async (url) => {
    setBusy("scan"); setErr(""); setScanStatus("🔎 Analyse de la pancarte en cours…");
    try {
      const p = await post("/prospection/scan", { image_url: url });
      setCur(p);
      setChannel((p.phone && !p.email) ? "whatsapp" : (p.email && !p.phone) ? "email" : "");
      setScanStatus("✓ Informations récupérées"); setRetryUrl(""); setStep(1); load();
    } catch (_) {
      setScanStatus(""); setRetryUrl(url);
      setErr("Nous n'avons pas réussi à analyser cette photo. Vos informations sont conservées. Essayez avec une photo plus nette.");
    }
    setBusy("");
  };

  const onScan = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setErr(""); setRetryUrl("");
    if (files.length === 1) {
      setBusy("scan"); setScanStatus("📸 Photo ajoutée ✓ — envoi…");
      let uploadedUrl = "";
      try {
        const compressed = await compressImage(files[0]);
        const up = await uploadMemberFile(compressed);
        uploadedUrl = up.url;
        setScanStatus("🔎 Analyse de la pancarte en cours…");
        const p = await post("/prospection/scan", { image_url: uploadedUrl });
        setCur(p);
        setChannel((p.phone && !p.email) ? "whatsapp" : (p.email && !p.phone) ? "email" : "");
        setScanStatus("✓ Informations récupérées"); setStep(1); load();
      } catch (e2) {
        setScanStatus("");
        if (uploadedUrl) {
          setRetryUrl(uploadedUrl);
          setErr("Nous n'avons pas réussi à analyser cette photo. Vos informations sont conservées. Essayez avec une photo plus nette.");
        } else {
          setErr("L'envoi de la photo a échoué. Vérifiez votre connexion et réessayez.");
        }
      }
      setBusy("");
    } else {
      // Tournée terrain — scan multiple
      setBusy("batch"); setBatch({ done: 0, total: files.length, ok: 0 });
      let ok = 0;
      for (let i = 0; i < files.length; i++) {
        try { await scanOne(files[i]); ok++; } catch (_) {}
        setBatch({ done: i + 1, total: files.length, ok });
      }
      setBusy(""); setBatch(null);
      load(); resetWizard(); setView("list");
    }
  };

  const saveFields = async () => {
    if (!cur) return;
    setBusy("save"); setErr("");
    try {
      const p = await fetch(`${API}/prospection/prospects/${cur.id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: cur.first_name, last_name: cur.last_name, company: cur.company, phone: cur.phone, email: cur.email, sector: cur.sector, city: cur.city }),
      }).then((r) => r.json());
      setCur(p);
      const single = (p.phone && !p.email) ? "whatsapp" : (p.email && !p.phone) ? "email" : channel;
      setChannel(single);
      setStep(2);
      load();
    } catch (e2) { setErr("Enregistrement impossible."); }
    setBusy("");
  };

  const generate = async (nextTone) => {
    const t = nextTone || tone;
    setBusy("gen"); setErr("");
    try {
      const g = await post("/prospection/generate", { prospect_id: cur.id, channel, intent, tone: t });
      setGen(g); setTone(t); setStep(4);
    } catch (e2) { setErr(e2.message || "Génération impossible."); }
    setBusy("");
  };

  const send = async () => {
    if (!gen) return;
    const msg = gen.message || "";
    if (channel === "whatsapp") {
      const num = cleanPhoneIntl(cur.phone);
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
    } else {
      const subject = encodeURIComponent(gen.subject || "OFF MARKETTE");
      window.open(`mailto:${encodeURIComponent(cur.email || "")}?subject=${subject}&body=${encodeURIComponent(msg)}`, "_self");
    }
    try {
      await post("/prospection/log-send", { prospect_id: cur.id, channel, subject: gen.subject, content: msg });
    } catch (_) {}
    load();
    resetWizard(); setView("list");
  };

  const removeProspect = async (id) => {
    try { await fetch(`${API}/prospection/prospects/${id}`, { method: "DELETE", credentials: "include" }); } catch (_) {}
    setDetail(null); load();
  };

  const name = (p) => `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.company || "Prospect";
  const canWhatsapp = !!(cur && cur.phone);
  const canEmail = !!(cur && cur.email);

  // -------- WIZARD --------
  if (view === "wizard") {
    const steps = ["Scanner", "Vérifier", "Canal", "Intention", "Message"];
    return (
      <div className="max-w-xl mx-auto" data-testid="prospection-wizard">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => { resetWizard(); setView("list"); }} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-[#111827]" data-testid="prospection-wizard-back">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="flex items-center gap-1.5">
            {steps.map((s, i) => (
              <span key={s} className={`h-1.5 rounded-full transition-all ${i <= step ? "bg-[#C19A6B] w-6" : "bg-zinc-300 w-3"}`} />
            ))}
          </div>
        </div>

        {err && <p className="mb-4 text-sm text-red-500" data-testid="prospection-error">{err}</p>}

        <AnimatePresence mode="wait">
          {/* STEP 0 — Scan */}
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#C19A6B]/15 flex items-center justify-center"><MapPin className="w-7 h-7 text-[#C19A6B]" /></div>
              <h3 className="calli text-3xl sm:text-4xl text-[#C19A6B] leading-tight">Scanner un professionnel</h3>
              <p className="text-sm text-zinc-500">Photographiez une pancarte « à vendre » ou une carte de visite. L'IA extrait automatiquement les coordonnées.</p>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <label className="block cursor-pointer" data-testid="prospection-scan-camera">
                  <div className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#C19A6B]/50 bg-[#C19A6B]/5 hover:bg-[#C19A6B]/10 py-7 rounded-[6px] transition-colors">
                    {busy ? <Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /> : <Camera className="w-6 h-6 text-[#C19A6B]" />}
                    <span className="text-xs font-medium uppercase tracking-wider text-[#C19A6B]">Prendre une photo</span>
                  </div>
                  <input type="file" accept="image/*" capture="environment" onChange={onScan} className="hidden" disabled={!!busy} />
                </label>
                <label className="block cursor-pointer" data-testid="prospection-scan-gallery">
                  <div className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#C19A6B]/50 bg-[#C19A6B]/5 hover:bg-[#C19A6B]/10 py-7 rounded-[6px] transition-colors">
                    {busy ? <Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /> : <ImageIcon className="w-6 h-6 text-[#C19A6B]" />}
                    <span className="text-xs font-medium uppercase tracking-wider text-[#C19A6B]">Importer / plusieurs</span>
                  </div>
                  <input type="file" accept="image/*" multiple onChange={onScan} className="hidden" disabled={!!busy} />
                </label>
              </div>
              <p className="text-[11px] text-zinc-400 flex items-center justify-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Tournée terrain : importez plusieurs photos d'un coup, l'IA les traite en série.</p>
              {scanStatus && <p className="text-xs font-medium text-[#C19A6B]" data-testid="prospection-scan-status">{scanStatus}</p>}
              {busy === "batch" && batch && <p className="text-xs text-[#C19A6B] font-medium" data-testid="prospection-batch-progress">Analyse {batch.done}/{batch.total} — {batch.ok} ajouté(s)…</p>}
              {retryUrl && !busy && (
                <button onClick={() => analyzeUrl(retryUrl)} data-testid="prospection-retry" className="inline-flex items-center gap-2 mx-auto px-4 py-2 rounded-lg bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-semibold uppercase tracking-wider transition-colors">Réessayer</button>
              )}
            </motion.div>
          )}

          {/* STEP 1 — Vérifier */}
          {step === 1 && cur && (
            <motion.div key="s1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="flex items-center gap-3">
                {cur.photo && <img src={cur.photo} alt="" className="w-16 h-16 object-cover rounded-[6px] border border-[#E5E7EB]" />}
                <div>
                  <h3 className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-tight">Vérifiez les informations</h3>
                  <p className="text-xs text-zinc-500">Corrigez si besoin avant de continuer.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prénom" value={cur.first_name} onChange={(v) => setCur({ ...cur, first_name: v })} testid="prospection-f-first" />
                <Field label="Nom" value={cur.last_name} onChange={(v) => setCur({ ...cur, last_name: v })} testid="prospection-f-last" />
              </div>
              <Field label="Société" value={cur.company} onChange={(v) => setCur({ ...cur, company: v })} testid="prospection-f-company" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Téléphone" value={cur.phone} onChange={(v) => setCur({ ...cur, phone: v })} testid="prospection-f-phone" />
                <Field label="Email" value={cur.email} onChange={(v) => setCur({ ...cur, email: v })} testid="prospection-f-email" type="email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Métier / secteur" value={cur.sector} onChange={(v) => setCur({ ...cur, sector: v })} testid="prospection-f-sector" />
                <Field label="Ville" value={cur.city} onChange={(v) => setCur({ ...cur, city: v })} testid="prospection-f-city" />
              </div>
              <button onClick={saveFields} disabled={busy === "save"} data-testid="prospection-review-next"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-semibold uppercase tracking-wider py-3 rounded-[4px] transition-colors">
                {busy === "save" ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continuer <ChevronRight className="w-4 h-4" /></>}
              </button>
            </motion.div>
          )}

          {/* STEP 2 — Canal */}
          {step === 2 && cur && (
            <motion.div key="s2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4 text-center">
              <h3 className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-tight">Comment contacter {name(cur)} ?</h3>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => { if (canWhatsapp) { setChannel("whatsapp"); setStep(3); } }} disabled={!canWhatsapp} data-testid="prospection-channel-whatsapp"
                  className={`flex flex-col items-center gap-2 py-6 rounded-[6px] border-2 transition-colors ${canWhatsapp ? "border-[#25D366]/40 bg-[#25D366]/5 hover:bg-[#25D366]/10 text-[#128C7E]" : "border-zinc-200 bg-zinc-50 text-zinc-300 cursor-not-allowed"}`}>
                  <MessageCircle className="w-7 h-7" />
                  <span className="text-sm font-semibold uppercase tracking-wider">WhatsApp</span>
                  <span className="text-[11px]">{canWhatsapp ? cur.phone : "Aucun numéro"}</span>
                </button>
                <button onClick={() => { if (canEmail) { setChannel("email"); setStep(3); } }} disabled={!canEmail} data-testid="prospection-channel-email"
                  className={`flex flex-col items-center gap-2 py-6 rounded-[6px] border-2 transition-colors ${canEmail ? "border-[#C19A6B]/40 bg-[#C19A6B]/5 hover:bg-[#C19A6B]/10 text-[#C19A6B]" : "border-zinc-200 bg-zinc-50 text-zinc-300 cursor-not-allowed"}`}>
                  <Mail className="w-7 h-7" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Email</span>
                  <span className="text-[11px] truncate max-w-[140px]">{canEmail ? cur.email : "Aucun email"}</span>
                </button>
              </div>
              {!canWhatsapp && !canEmail && <p className="text-sm text-red-500">Aucune coordonnée détectée. Revenez en arrière pour saisir un téléphone ou un email.</p>}
            </motion.div>
          )}

          {/* STEP 3 — Intention (dictée) */}
          {step === 3 && cur && (
            <motion.div key="s3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-[#C19A6B]/15 flex items-center justify-center mb-2"><Sparkles className="w-6 h-6 text-[#C19A6B]" /></div>
                <h3 className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-tight">Dictez votre intention</h3>
                <p className="text-sm text-zinc-500">Expliquez simplement ce que vous voulez dire — l'IA rédigera le message {channel === "whatsapp" ? "WhatsApp" : "email"}.</p>
              </div>
              <VoiceTextBox value={intent} onChange={setIntent}
                placeholder="Ex : Je viens de voir sa pancarte, je voudrais lui présenter OFF MARKETTE et lui proposer de rejoindre mon réseau."
                testid="prospection-intent" minHeight="min-h-[120px]" />
              <button onClick={() => generate()} disabled={busy === "gen" || intent.trim().length < 5} data-testid="prospection-generate-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-semibold uppercase tracking-wider py-3 rounded-[4px] transition-colors">
                {busy === "gen" ? <><Loader2 className="w-5 h-5 animate-spin" /> Génération…</> : <><Sparkles className="w-5 h-5" /> Générer le message</>}
              </button>
            </motion.div>
          )}

          {/* STEP 4 — Message */}
          {step === 4 && gen && cur && (
            <motion.div key="s4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <h3 className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-tight">Votre message {channel === "whatsapp" ? "WhatsApp" : "email"}</h3>
              {channel === "email" && (
                <Field label="Objet" value={gen.subject} onChange={(v) => setGen({ ...gen, subject: v })} testid="prospection-subject" />
              )}
              <textarea value={gen.message} onChange={(e) => setGen({ ...gen, message: e.target.value })} data-testid="prospection-message"
                className="w-full bg-white border border-[#E5E7EB] px-3 py-3 text-sm outline-none focus:border-[#C19A6B] rounded-[4px] min-h-[220px] leading-relaxed" />
              <div className="space-y-2">
                <span className="text-[11px] uppercase tracking-wider text-zinc-500">Changer le ton & régénérer</span>
                <div className="flex flex-wrap gap-2">
                  {TONES.map((t) => (
                    <button key={t.key} onClick={() => generate(t.key)} disabled={busy === "gen"} data-testid={`prospection-tone-${t.key}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tone === t.key ? "bg-[#C19A6B] border-[#C19A6B] text-white" : "border-zinc-300 text-zinc-500 hover:border-[#C19A6B] hover:text-[#C19A6B]"}`}>
                      {t.label}
                    </button>
                  ))}
                  <button onClick={() => generate()} disabled={busy === "gen"} data-testid="prospection-regenerate"
                    className="text-xs px-3 py-1.5 rounded-full border border-zinc-300 text-zinc-500 hover:border-[#C19A6B] hover:text-[#C19A6B] inline-flex items-center gap-1">
                    {busy === "gen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Nouvelle version
                  </button>
                </div>
              </div>
              <button onClick={send} data-testid="prospection-send"
                className={`w-full inline-flex items-center justify-center gap-2 text-white font-semibold uppercase tracking-wider py-3.5 rounded-[4px] transition-colors ${channel === "whatsapp" ? "bg-[#25D366] hover:bg-[#20bd5a]" : "bg-[#C19A6B] hover:bg-[#d1af84]"}`}>
                {channel === "whatsapp" ? <MessageCircle className="w-5 h-5" /> : <Mail className="w-5 h-5" />}
                Envoyer via {channel === "whatsapp" ? "WhatsApp" : "Email"}
              </button>
              {cur.phone && (
                <a href={`tel:${(cur.phone || "").replace(/[^\d+]/g, "")}`} data-testid="prospection-call"
                  className="group relative w-full inline-flex items-center justify-center overflow-hidden border-2 border-[#C19A6B] bg-white text-[#C19A6B] font-semibold uppercase tracking-wider py-3 rounded-full transition-colors duration-300 hover:text-white">
                  <span className="absolute inset-0 bg-[#C19A6B] translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                  <span className="relative inline-flex items-center gap-2"><Phone className="w-5 h-5" /> Appeler {cur.phone}</span>
                </a>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // -------- DETAIL (historique) --------
  if (detail) {
    return (
      <div className="max-w-xl mx-auto" data-testid="prospection-detail">
        <button onClick={() => setDetail(null)} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-[#111827] mb-4"><ArrowLeft className="w-4 h-4" /> Retour</button>
        <div className="bg-white border border-[#E5E7EB] rounded-[6px] p-5 space-y-3">
          <div className="flex items-center gap-3">
            {detail.photo && <img src={detail.photo} alt="" className="w-14 h-14 object-cover rounded-[6px]" />}
            <div>
              <h3 className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-tight">{name(detail)}</h3>
              <p className="text-xs text-zinc-500">{[detail.sector, detail.city].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          <div className="text-sm text-zinc-600 space-y-1">
            {detail.company && <p className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#C19A6B]" /> {detail.company}</p>}
            {detail.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#C19A6B]" /> {detail.phone}</p>}
            {detail.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#C19A6B]" /> {detail.email}</p>}
          </div>
          {detail.phone && (
            <a href={`tel:${(detail.phone || "").replace(/[^\d+]/g, "")}`} data-testid="prospection-detail-call"
              className="group relative mt-2 inline-flex items-center justify-center overflow-hidden border-2 border-[#C19A6B] bg-white text-[#C19A6B] text-sm font-semibold uppercase tracking-wider px-5 py-2 rounded-full transition-colors duration-300 hover:text-white">
              <span className="absolute inset-0 bg-[#C19A6B] translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
              <span className="relative inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Appeler</span>
            </a>
          )}
        </div>
        <h4 className="text-xs uppercase tracking-wider text-zinc-500 mt-6 mb-2">Suivi</h4>
        <div className="bg-white border border-[#E5E7EB] rounded-[6px] p-5 space-y-4">
          <div>
            <span className="text-[11px] uppercase tracking-wider text-zinc-500">Statut</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {STATUSES.map((s) => (
                <button key={s.key} onClick={() => patchProspect(detail.id, { status: s.key })} data-testid={`prospection-status-${s.key}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${detail.status === s.key ? s.cls + " border-transparent font-semibold" : "border-zinc-300 text-zinc-500 hover:border-[#C19A6B] hover:text-[#C19A6B]"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wider text-zinc-500">Date de relance</span>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <input type="date" value={detail.relance_date || ""} onChange={(e) => patchProspect(detail.id, { relance_date: e.target.value })} data-testid="prospection-relance-date"
                className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#C19A6B] rounded-[4px]" />
              {relanceDue(detail) && <span className="text-xs text-red-500 font-medium inline-flex items-center gap-1" data-testid="prospection-relance-due"><Bell className="w-3.5 h-3.5" /> À relancer !</span>}
              {detail.relance_date && <button onClick={() => patchProspect(detail.id, { relance_date: "" })} className="text-xs text-zinc-400 hover:text-zinc-600">Effacer</button>}
            </div>
          </div>
        </div>
        <h4 className="text-xs uppercase tracking-wider text-zinc-500 mt-6 mb-2">Historique des échanges</h4>
        {(detail.history || []).length === 0 ? (
          <p className="text-sm text-zinc-400">Aucun message envoyé pour l'instant.</p>
        ) : (
          <div className="space-y-3">
            {[...(detail.history || [])].reverse().map((h, i) => (
              <div key={i} className="bg-white border border-[#E5E7EB] rounded-[6px] p-4">
                <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-2">
                  <span className="inline-flex items-center gap-1 uppercase tracking-wider text-[#C19A6B]">
                    {h.channel === "whatsapp" ? <MessageCircle className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />} {h.channel}
                  </span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(h.date).toLocaleString("fr-FR")}</span>
                </div>
                {h.subject && <p className="text-sm font-medium text-[#111827] mb-1">{h.subject}</p>}
                <p className="text-sm text-zinc-600 whitespace-pre-wrap">{h.content}</p>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => removeProspect(detail.id)} className="mt-6 inline-flex items-center gap-2 text-xs text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /> Supprimer ce prospect</button>
      </div>
    );
  }

  // -------- LIST --------
  return (
    <div data-testid="prospection-list">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="calli text-3xl sm:text-4xl text-[#C19A6B] leading-tight">Agents à contacter</h2>
          <p className="text-sm text-zinc-500">Scannez une pancarte ou une carte de visite et laissez l'IA préparer votre message.</p>
        </div>
        <button onClick={() => { resetWizard(); setView("wizard"); }} data-testid="prospection-new"
          className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm font-semibold uppercase tracking-wider px-4 py-2.5 rounded-[4px] transition-colors">
          <Camera className="w-4 h-4" /> Scanner
        </button>
      </div>

      {!loading && prospects.filter(relanceDue).length > 0 && (
        <div className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-[6px]" data-testid="prospection-relance-banner">
          <Bell className="w-4 h-4" /> {prospects.filter(relanceDue).length} prospect(s) à relancer aujourd'hui ou en retard.
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : prospects.length === 0 ? (
        <div className="py-16 text-center border-2 border-dashed border-[#E5E7EB] rounded-[8px]">
          <MapPin className="w-8 h-8 text-[#C19A6B] mx-auto mb-3" />
          <p className="text-sm text-zinc-500">Aucun professionnel scanné pour l'instant.</p>
          <button onClick={() => { resetWizard(); setView("wizard"); }} className="mt-4 inline-flex items-center gap-2 text-sm text-[#C19A6B] font-semibold uppercase tracking-wider">
            <Camera className="w-4 h-4" /> Scanner un professionnel
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {prospects.map((p) => (
            <button key={p.id} onClick={() => setDetail(p)} data-testid={`prospection-card-${p.id}`}
              className="text-left bg-white border border-[#E5E7EB] hover:border-[#C19A6B]/50 rounded-[6px] p-4 transition-colors flex items-center gap-3">
              {p.photo ? <img src={p.photo} alt="" className="w-12 h-12 object-cover rounded-[6px]" /> : <div className="w-12 h-12 rounded-[6px] bg-[#C19A6B]/10 flex items-center justify-center text-[#C19A6B] font-semibold">{name(p)[0]}</div>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#111827] truncate">{name(p)}</p>
                <p className="text-[11px] text-zinc-500 truncate">{[p.company, p.city].filter(Boolean).join(" · ") || p.sector || "—"}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {relanceDue(p) && <span className="text-[10px] text-red-500 font-semibold inline-flex items-center gap-1"><Bell className="w-3 h-3" /> Relance</span>}
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${statusMeta(p.status).cls}`}>
                  {statusMeta(p.status).label}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
